import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import EmergencyWarning from '../components/EmergencyWarning';
import {
  Video,
  MessageSquare,
  Pill,
  Clock,
  Download,
  CheckCircle,
  ArrowRight,
  Smartphone,
  Calendar,
  CreditCard
} from 'lucide-react';

const BookAppointment = () => {
  const visitTypes = [
    {
      icon: Video,
      title: 'Virtual Urgent Care',
      duration: '20 minutes',
      description: 'Video consultation for new concerns and symptoms'
    },
    {
      icon: MessageSquare,
      title: 'Follow-Up Consultation',
      duration: '15 minutes',
      description: 'Quick check-in for existing patients'
    },
    {
      icon: Pill,
      title: 'Async Refill Request',
      duration: 'Message-based',
      description: 'Request medication refills via secure message'
    }
  ];

  const steps = [
    {
      icon: Download,
      title: 'Download Spruce Health',
      description: 'Click the button below to get the app and connect with us'
    },
    {
      icon: Calendar,
      title: 'Receive Booking Link',
      description: 'We\'ll send you our calendar link through Spruce'
    },
    {
      icon: CreditCard,
      title: 'Complete Payment',
      description: 'Pay securely through the link we send via Spruce'
    },
    {
      icon: Video,
      title: 'Join Your Visit',
      description: 'Attend your video consultation in the Spruce app'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-12" data-testid="book-appointment-page">
      {/* Background Logo */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none opacity-100" style={{ zIndex: 0 }}>
        <img 
          alt="" 
          className="w-[120vmin] h-[120vmin] object-contain"
          src="https://customer-assets.emergentagent.com/job_53640627-9830-4adc-91a5-529f85e3f30c/artifacts/pm4tpcen_IWMC_logo_transparent.png"
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block bg-white/95 backdrop-blur-sm px-8 py-6 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
            <div className="inline-flex items-center gap-2 bg-[#1E3A8A]/10 text-[#1E3A8A] px-4 py-2 rounded-full text-sm font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              <Smartphone className="w-4 h-4" />
              TELEMEDICINE-ONLY SERVICE
            </div>
            <h1 className="text-3xl lg:text-4xl font-black text-[#1E3A8A] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
              Book a Virtual Visit
            </h1>
            <p className="text-slate-600 font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
              All bookings and payments are handled through the Spruce Health app
            </p>
          </div>
        </div>

        {/* Emergency Warning */}
        <div className="mb-8">
          <EmergencyWarning compact />
        </div>

        {/* Main CTA - Download Spruce */}
        <div className="mb-12">
          <Card className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white border-0 shadow-2xl">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl lg:text-3xl font-black mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
                Step 1: Download Spruce Health App
              </h2>
              <p className="text-white/90 text-lg mb-6 font-bold max-w-2xl mx-auto" style={{ fontFamily: 'Merriweather, serif' }}>
                To book an appointment, you must first download the Spruce Health app and connect with Integrated Wellness. 
                We'll send you the calendar link and payment options directly through Spruce.
              </p>
              <a 
                href="https://spruce.care/integratedwellness" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-white text-[#1E3A8A] px-10 py-5 rounded-lg font-black text-xl hover:bg-white/90 transition-colors shadow-lg"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                <Download className="w-7 h-7" />
                Download Spruce Health App
              </a>
            </CardContent>
          </Card>
        </div>

        {/* How Booking Works */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <div className="inline-block bg-white/95 backdrop-blur-sm px-6 py-4 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
              <h2 className="text-2xl font-black text-[#1E3A8A]" style={{ fontFamily: 'Merriweather, serif' }}>
                How Booking Works
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div 
                key={index}
                className="bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-xl border-2 border-[#1E3A8A] text-center"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#3B82F6] to-[#1E3A8A] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <step.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-black text-[#1E3A8A] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>{step.title}</h3>
                <p className="text-slate-600 text-sm font-bold" style={{ fontFamily: 'Merriweather, serif' }}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Visit Types */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <div className="inline-block bg-white/95 backdrop-blur-sm px-6 py-4 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
              <h2 className="text-2xl font-black text-[#1E3A8A]" style={{ fontFamily: 'Merriweather, serif' }}>
                Available Visit Types
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visitTypes.map((type, index) => (
              <div 
                key={index}
                className="bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-xl border-2 border-[#1E3A8A] text-center"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#3B82F6] to-[#1E3A8A] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <type.icon className="w-7 h-7 text-white" />
                </div>
                <div className="inline-block px-3 py-1 bg-[#3B82F6]/20 text-[#1E3A8A] text-sm font-bold rounded-full mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
                  <Clock className="w-4 h-4 inline mr-1" />
                  {type.duration}
                </div>
                <h3 className="font-black text-[#1E3A8A] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>{type.title}</h3>
                <p className="text-slate-600 text-sm font-bold" style={{ fontFamily: 'Merriweather, serif' }}>{type.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Already have Spruce? */}
        <div className="text-center">
          <Card className="bg-white/95 backdrop-blur-sm border-2 border-[#1E3A8A] shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-xl font-black text-[#1E3A8A] mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
                Already Have Spruce Health?
              </h3>
              <p className="text-slate-600 font-bold mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
                If you've already downloaded Spruce and connected with us, simply send us a message 
                through the app to request an appointment. We'll respond with available times and payment options.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="https://spruce.care/integratedwellness" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg"
                  style={{ 
                    background: 'linear-gradient(to right, #1E3A8A, #3B82F6)',
                    fontFamily: 'Merriweather, serif'
                  }}
                >
                  <MessageSquare className="w-5 h-5" />
                  Open Spruce App
                </a>
                <a href="tel:2015353582">
                  <button 
                    className="inline-flex items-center gap-2 text-[#1E3A8A] border-2 border-[#1E3A8A] px-8 py-4 rounded-lg font-bold text-lg transition-all hover:bg-[#1E3A8A]/5"
                    style={{ fontFamily: 'Merriweather, serif' }}
                  >
                    Call (201) 535-3582
                  </button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BookAppointment;
